import React, {useEffect, useMemo, useState} from 'react';
import {Box, Flex, HStack, Link, useTheme} from '@chakra-ui/react';
import {Link as DomLink, useNavigate} from 'react-router-dom';
import {ROUTES} from '../../constants/routes';
import {Event, Offer} from '../../types/article';
import {H2, H3, TextG10, TextG12, TextG14} from '../../components/typography';
import {BlueButton, WhiteButton} from '../../components/button';
import {calculateDistance, getDistanceString} from '../../utils/distance';
import {registerTicketUrlClick, updateEvent} from '../../endpoints/api';
import useGetUserLocation from '../../hooks/useUserLocation';
import {getEventDate} from '../../utils/date';
import {getDisplayPrice} from '../../utils/price';
import Icon from '../../components/Icon';
import {storeScrollPositionInSession} from '../../utils/scroll';
import IconsHeader from './About/IconsHeader';
import ConvinceMeToGoButton from './ConvinceMeToGoButton';
import EventCountdown from './EventCountdown';
import {EventRSVPStatus, useFetchEventRSVPStats, useSetAttendance} from '../../hooks/useEvent';
import {generatePath, useSearchParams} from 'react-router-dom';
import DotSeparator from '../../components/DotSeparator';
import useCountdown from '../../hooks/useCountdown';
import useCheckIn from '../../hooks/useCheckIn';
import PromptModal from '../../components/Modals/PromptModal';
import useActiveRedemptions from '../../hooks/useActiveRedemptions';
import CardBadge from '../../components/CardBadge';
import RSVPController from './RSVP/RSVPController';
import useAuth from '../../hooks/useAuth';
import UseablePassPill from '../../components/Articles/EventCard/UseablePassPill';
import ExternalLink from '../../components/ExternalLink';
import {useFetchUserTickets} from '../../hooks/useUserTickets';
import {filter} from 'lodash';
import NotWalkaboutAffiliatedPill from '../../components/Articles/EventCard/NotWalkaboutAffiliatedPill';
import InterestedToggle from '../../components/InterestedToggle';
import PointBoostBadge from '../../components/Articles/PlaceCard/PointBoostBadge';
import RequiresEarlyAccess from '../../components/RequiresEarlyAccess';
import Modal from '../../components/Modals/Modal';
import MeetupForm from '../CreateMeetup/MeetupForm';
import {ToastMessage} from '../../components/toast';
import EventInterestedGoingDisplay from '../../components/UsersList/EventInterestedGoingDisplay';
import {InlineEditableField} from '../../components/InlineEditableField';
import {EntityRole, useGetCurrentUserRoleForEntity} from '../../hooks/useEntityUserRole';
import {useGeneralSettings} from '../../hooks/useGeneralSettings';

const Header: React.FC<{
  event: Event;
  eventOffers: Array<Offer>;
  refetchEvent: () => void;
  disableLocation: () => void;
}> = ({event, eventOffers, refetchEvent, disableLocation}) => {
  const theme = useTheme();
  const userLocation = useGetUserLocation();
  const {startPassed, endPassed, canCheckIn} = useCountdown(event);
  const [isClosedModel, setIsClosedModel] = useState(() => event.rsvp_status.status_value === null);
  const {mutateAsync} = useSetAttendance();
  const [searchParams] = useSearchParams();
  const {data} = useFetchEventRSVPStats(Number(event.id || searchParams.get('id')));
  const rsvp_stats = data?.data.rsvp_stats;
  const {eventCheckIn} = useCheckIn();
  const [promptOpen, setPromptOpen] = useState(false);
  const {activeRedemptions} = useActiveRedemptions();
  const {user} = useAuth();
  const eventDiscount = user?.activeMembershipPlan?.event_discount;
  const navigate = useNavigate();
  const [showMeetupModal, setShowMeetupModal] = useState(false);
  const [showInsidersModal, setShowInsidersModal] = useState(false);

  const fetchTickets = useFetchUserTickets(!!user);
  const tickets = fetchTickets.data?.data;
  const eventTickets = filter(tickets, (ticket) => ticket.event_id == event.id);
  const userHasTickets = eventTickets && eventTickets.length > 0;

  const action = useMemo(() => searchParams.get('action'), [searchParams]);
  const isLoading = userLocation.isLoading;

  const isWalkaboutEvent = Boolean(event.is_walkabout_official);
  const interestedAndGoing = rsvp_stats?.INTERESTED + rsvp_stats?.GOING;

  const isProduction = process.env.NODE_ENV === 'production';
  const musicFestEventId = isProduction ? '48243' : '39628';

  // Calculate distance to event place for "I'm Already Here" functionality
  const distanceToEvent = useMemo(() => {
    if (
      userLocation.data?.latitude &&
      userLocation.data?.longitude &&
      event.place?.latitude &&
      event.place?.longitude
    ) {
      return calculateDistance(
        {latitude: event.place.latitude, longitude: event.place.longitude},
        userLocation.data,
      );
    }
    return null;
  }, [userLocation.data, event.place]);

  // Show "I'm Already Here"
  const showAlreadyHereOption = useMemo(() => {
    return !event.has_checked_in && canCheckIn && !endPassed;
  }, [event.has_checked_in, canCheckIn, endPassed]);

  useEffect(() => {
    if (!userLocation.isLoading && action && action == 'check-in') {
      const qr_code_id_string = searchParams.get('qr_code_id');
      let qr_code_id;
      if (qr_code_id_string) {
        qr_code_id = parseInt(qr_code_id_string);
      }
      handleCheckIn({sdv: !!searchParams.get('sdv'), qr_code_id});
    }
  }, [action, isLoading]);

  const eventDate = useMemo(
    () =>
      !!event.is_recurring && event.recurring_schedule
        ? event.recurring_schedule
        : getEventDate(event.start_date_time, event.end_date_time),
    [event],
  );

  const iconStyle: React.CSSProperties = useMemo(
    () => ({
      color: theme.colors.grey,
      fontSize: '12px',
      marginRight: '5px',
    }),
    [theme],
  );

  const statusValue = event.rsvp_status.status_value || EventRSVPStatus.NO_STATUS;

  const {data: currentRole} = useGetCurrentUserRoleForEntity('events', event.id!);
  const canEditEvent =
    currentRole?.data === EntityRole.MANAGER || currentRole?.data === EntityRole.TEAM_MEMBER;

  const handleCheckIn = ({sdv, qr_code_id}) => {
    if (event.has_checked_in) {
      return;
    }

    if (!canCheckIn) {
      setPromptOpen(true);
      return;
    }

    if (userLocation.error && !sdv) {
      disableLocation();
      return;
    }

    if ((!userLocation.data?.latitude || !userLocation.data?.longitude) && !sdv) {
      ToastMessage({
        status: 'error',
        text: 'Unable to get your location. Please enable location services to check-in',
      });
      return;
    }

    eventCheckIn({
      event: event,
      eventOffers: eventOffers,
      activeRedemptions: activeRedemptions,
      latitude: userLocation?.data?.latitude,
      longitude: userLocation?.data?.longitude,
      isPublic: true,
      sdv: sdv,
      qr_code_id,
    });
  };

  // Handler for "I'm Already Here" - bypasses location check
  const handleAlreadyHereCheckIn = () => {
    if (event.has_checked_in) return;

    eventCheckIn({
      event: event,
      eventOffers: eventOffers,
      activeRedemptions: activeRedemptions,
      latitude: userLocation?.data?.latitude,
      longitude: userLocation?.data?.longitude,
      isPublic: true,
      sdv: true,
      qr_code_id: undefined,
    });
  };

  return (
    <>
      <PromptModal
        title={
          'You can Check-In when the Event starts. Would you like to set a reminder for this Event?'
        }
        isOpen={promptOpen}
        declineText={'No'}
        acceptText={'Yes'}
        declineCallback={() => setPromptOpen(false)}
        acceptCallback={(e) => {
          if (
            !(
              event.rsvp_status.status_value == EventRSVPStatus.GOING ||
              event.rsvp_status.status_value == EventRSVPStatus.INTERESTED
            )
          ) {
            mutateAsync({
              eventId: event.id,
              prevStatus: statusValue,
              status: EventRSVPStatus.INTERESTED,
            });
          }
          setPromptOpen(false);
        }}
      />
      <Flex
        id="rsvp-tooltip-highlight"
        position="relative"
        top="0"
        width="90%"
        marginLeft="5%"
        transform="auto"
        translateY="-90px"
        marginBottom="-5rem"
        backgroundColor="white"
        boxShadow={`0 0 12px ${theme.colors.grey}`}
        borderRadius=".5rem"
        p="0.75rem"
        pb="0"
        flexDirection={'column'}>
        {event.is_walkabout_official ? (
          <CardBadge position="absolute" top="-6px" label="WALKABOUT OFFICIAL" center="true" />
        ) : null}
        <Flex width="100%" flexDirection="column" mt={event.is_walkabout_official ? '8px' : 0}>
          <Flex direction="column" gap="5px">
            <HStack>
              {/* {!!event.is_recurring && <Icon iconName="fi-rr-time-past" style={iconStyle} />} */}
              <TextG10
                ml="0 !important"
                color={theme.colors.grey}
                isTruncated="&hellip;"
                noOfLines={1}>
                {!!event.is_recurring ? `${eventDate} (Recurring)` : eventDate}
              </TextG10>
            </HStack>
            <InlineEditableField
              value={event.title}
              canEdit={canEditEvent}
              onSave={(title) =>
                updateEvent(event.id!, {title})
                  .then(() => refetchEvent())
                  .catch((e) => {
                    ToastMessage({
                      status: 'error',
                      text: e.message,
                    });
                  })
              }
              renderDisplay={(value) => <H3>{value}</H3>}
            />
            {!isWalkaboutEvent && <NotWalkaboutAffiliatedPill />}
            <PointBoostBadge article={event} />
            <UseablePassPill event={event} />
            {event.place && (
              <HStack mt="4px">
                <DomLink
                  style={{
                    color: '#276ACD',
                    fontWeight: '500',
                    fontFamily: 'Gordita',
                    fontSize: '12px',
                  }}
                  to={generatePath(ROUTES.PLACE, {
                    placeId: event.place.id.toString(),
                  })}
                  onClick={() => storeScrollPositionInSession()}>
                  <TextG12>{event.place.name}</TextG12>
                </DomLink>
                <DotSeparator color="black" />
                {userLocation.data?.latitude &&
                  userLocation.data.longitude &&
                  event.place?.latitude &&
                  event.place?.longitude && (
                    <TextG12 m="0 !important" noOfLines={1} minWidth="90px">
                      {getDistanceString(
                        calculateDistance(
                          {latitude: event.place?.latitude, longitude: event.place?.longitude},
                          userLocation.data,
                        ),
                      )}{' '}
                      away
                    </TextG12>
                  )}
              </HStack>
            )}
          </Flex>
        </Flex>
        <Flex
          my={3}
          justifyContent={interestedAndGoing > 0 ? 'space-between' : 'flex-end'}
          h="25px"
          alignItems="center">
          {interestedAndGoing > 0 && <EventInterestedGoingDisplay eventId={Number(event.id)} />}

          <Flex gap="5px" whiteSpace="nowrap">
            <TextG12
              fontSize={event.has_tickets && eventDiscount && eventDiscount > 0 ? '10px' : '12px'}
              color={theme.colors.lightBlack}
              as={
                event.price > 0 &&
                event.has_tickets &&
                eventDiscount &&
                eventDiscount > 0 &&
                !event.price_override
                  ? 's'
                  : ''
              }>
              {getDisplayPrice(event.price, event?.max_price, event?.price_override)}
            </TextG12>
            {event.price > 0 &&
              event.has_tickets &&
              eventDiscount &&
              eventDiscount > 0 &&
              !event.price_override && (
                <TextG12 color={theme.colors.lightBlack}>
                  {getDisplayPrice(event.price - (event.price * eventDiscount) / 100)}
                </TextG12>
              )}
          </Flex>
          {/* <Flex flexShrink={0}>
            {(event.price || event?.price_override) && (
              <TextG14 sx={{marginTop: 'auto'}} fontWeight={500}>
                {getDisplayPrice(event.price, event?.max_price, event?.price_override)}
              </TextG14>
            )}
          </Flex> */}
        </Flex>{' '}
        <Flex direction={'column'} gap={'.5rem'}>
          {/* Music Fest Event Layout - Tickets and InterestedToggle in row */}
          {event.id.toString() === musicFestEventId ? (
            <>
              <BlueButton
                onClick={() => {
                  const element = document.getElementById('navigate');
                  if (element) {
                    element.scrollIntoView({behavior: 'smooth'});
                  }
                }}
                w="100%">
                <TextG14 fontWeight="500">Navigate the Fest</TextG14>
              </BlueButton>
              <Flex gap={'.5rem'} alignItems="center">
                {' '}
                <Box minW="50%">
                  {event.has_tickets ? (
                    <BlueButton
                      isDisabled={endPassed && !userHasTickets}
                      onClick={() => {
                        if (!user?.hasActiveMembership && !userHasTickets) {
                          setShowInsidersModal(true);
                        } else {
                          userHasTickets
                            ? navigate(`/tickets/${event.id}`)
                            : navigate(`/event/${event.id}/tickets`, {replace: true});
                        }
                      }}
                      w="100%"
                      border={endPassed ? '' : `1px solid ${theme.colors.blue}`}
                      flexGrow={1}>
                      <TextG12 fontWeight="500">
                        {userHasTickets ? 'See Tickets' : 'Get Tickets'}
                      </TextG12>
                    </BlueButton>
                  ) : event.tickets_url ? (
                    !user?.hasActiveMembership ? (
                      <BlueButton
                        w="100%"
                        border={`1px solid ${theme.colors.blue}`}
                        flexGrow={1}
                        onClick={() => setShowInsidersModal(true)}>
                        <TextG12 fontWeight="500">Get Tickets</TextG12>
                      </BlueButton>
                    ) : (
                      // <Link href={event.tickets_url} w="100%">
                      <BlueButton
                        w="100%"
                        border={`1px solid ${theme.colors.blue}`}
                        flexGrow={1}
                        onClick={() => {
                          // Extract just the pathname portion of the URL
                          const url = new URL(event.tickets_url);
                          const pathWithQuery = url.pathname + url.search;
                          navigate(pathWithQuery);
                          registerTicketUrlClick(event.id).catch((e) => console.log(e));
                        }}>
                        <TextG12 fontWeight="500">Get Tickets</TextG12>
                      </BlueButton>
                      // </Link>
                    )
                  ) : null}
                </Box>
                {!isWalkaboutEvent && (
                  <Box minWidth="50%">
                    <InterestedToggle event={event} />
                  </Box>
                )}
              </Flex>
            </>
          ) : (
            <Flex flexDir="column">
              {event.has_tickets ? (
                <BlueButton
                  isDisabled={endPassed && !userHasTickets}
                  onClick={() => {
                    userHasTickets
                      ? navigate(`/tickets/${event.id}`)
                      : navigate(`/event/${event.id}/tickets`, {replace: true});
                  }}
                  w="100%"
                  border={endPassed ? '' : `1px solid ${theme.colors.blue}`}
                  flexGrow={1}>
                  <TextG12 fontWeight="500">
                    {userHasTickets ? 'See Tickets' : 'Get Tickets'}
                  </TextG12>
                </BlueButton>
              ) : event.tickets_url ? (
                <ExternalLink href={event.tickets_url} w="100%" isExternal>
                  <BlueButton
                    w="100%"
                    border={`1px solid ${theme.colors.blue}`}
                    flexGrow={1}
                    onClick={() => {
                      registerTicketUrlClick(event.id).catch((e) => console.log(e));
                    }}>
                    <TextG12 fontWeight="500">Get Tickets</TextG12>
                    {/* {event.price && isFree(event.price) ? 'Get Tickets' : 'Buy Tickets'} */}
                  </BlueButton>
                </ExternalLink>
              ) : null}
              {!isWalkaboutEvent && (
                <Box mt="0.5rem">
                  <InterestedToggle event={event} />
                </Box>
              )}
            </Flex>
          )}
          {isWalkaboutEvent && (
            <Flex gap={'.5rem'} justifyContent="center" alignItems="center">
              {event.allow_check_in ? (
                event.rsvp_status.status_value === null || isClosedModel ? (
                  <ConvinceMeToGoButton
                    event={event}
                    typographySize="md"
                    modelClosedEvent={() => setIsClosedModel(false)}
                    textColor={event.rsvp_status.status_value === null ? 'blue' : 'black'}
                    background="white"
                    buttonProps={{
                      flexGrow: 1,
                      height: '2.5rem',
                      border: '1px solid blue',
                      width: '50%',
                      maxWidth: '50%',
                      minWidth: '50%',
                    }}
                  />
                ) : (
                  <WhiteButton
                    id="checkin-tooltip"
                    onClick={handleCheckIn}
                    w="50%"
                    minW="50%"
                    maxW="50%"
                    flexGrow={1}
                    borderRadius={'2.5rem'}
                    _hover={{background: ''}}
                    color={!canCheckIn ? theme.colors.lightestGrey : theme.colors.white}
                    background={
                      !canCheckIn
                        ? theme.colors.lighterGrey
                        : event.has_checked_in
                          ? 'green'
                          : theme.colors.blue
                    }
                    isDisabled={endPassed}>
                    {event.has_checked_in ? (
                      <Flex gap="6px" style={{color: 'white'}}>
                        <Icon iconName="fi-sr-check" style={{marginTop: '2px'}} />
                        <TextG12 fontWeight="500">Checked-In</TextG12>
                      </Flex>
                    ) : (
                      <TextG12 fontWeight="500">Check-In</TextG12>
                    )}
                  </WhiteButton>
                )
              ) : null}
              <Box w="100%" onClick={() => setIsClosedModel(false)}>
                <RSVPController
                  buttonProps={{
                    border: event.rsvp_status.status_value ? '1px solid black' : '1px solid black',
                    width: '100%',
                    maxWidth: '100%',
                    minWidth: '100%',
                  }}
                  event={event}
                />
              </Box>
            </Flex>
          )}
          {isWalkaboutEvent &&
            event.id.toString() === musicFestEventId &&
            user?.hasActiveMembership && (
              <Flex gap={'.5rem'} justifyContent="center" alignItems="center">
                {event.allow_check_in ? (
                  event.rsvp_status.status_value === null || isClosedModel ? (
                    <ConvinceMeToGoButton
                      event={event}
                      typographySize="md"
                      modelClosedEvent={() => setIsClosedModel(false)}
                      textColor={event.rsvp_status.status_value === null ? 'blue' : 'black'}
                      background="white"
                      buttonProps={{
                        flexGrow: 1,
                        height: '2.5rem',
                        border: '1px solid blue',
                        width: '100%',
                        maxWidth: '100%',
                        minWidth: '100%',
                      }}
                    />
                  ) : (
                    <WhiteButton
                      id="checkin-tooltip"
                      onClick={handleCheckIn}
                      w="100%"
                      minW="100%"
                      maxW="100%"
                      flexGrow={1}
                      borderRadius={'2.5rem'}
                      _hover={{background: ''}}
                      color={!canCheckIn ? theme.colors.lightestGrey : theme.colors.white}
                      background={
                        !canCheckIn
                          ? theme.colors.lighterGrey
                          : event.has_checked_in
                            ? 'green'
                            : theme.colors.blue
                      }
                      isDisabled={endPassed}>
                      {event.has_checked_in ? (
                        <Flex gap="6px" style={{color: 'white'}}>
                          <Icon iconName="fi-sr-check" style={{marginTop: '2px'}} />
                          <TextG12 fontWeight="500">Checked-In</TextG12>
                        </Flex>
                      ) : (
                        <TextG12 fontWeight="500">Check-In</TextG12>
                      )}
                    </WhiteButton>
                  )
                ) : null}
              </Flex>
            )}
        </Flex>{' '}
        {user?.hasActiveMembership &&
          event.id.toString() === musicFestEventId &&
          isWalkaboutEvent && (
            <Flex gap={'.5rem'} mt="8px">
              <Box w="100%" onClick={() => setIsClosedModel(false)}>
                <RSVPController
                  buttonProps={{
                    border: event.rsvp_status.status_value ? '1px solid black' : '1px solid black',
                    width: '100%',
                    maxWidth: '100%',
                    minWidth: '100%',
                  }}
                  event={event}
                />
              </Box>
              <BlueButton
                border={`1px solid ${theme.colors.blue}`}
                gap="8px"
                justifyContent="center"
                w="100%"
                textAlign={'center'}
                background={theme.colors.white}
                color={theme.colors.blue}
                onClick={() => {
                  // navigate(`/create-meetup?placeId=${event?.place?.id}&eventId=${event?.id}`);
                  setShowMeetupModal(true);
                }}>
                <Icon
                  style={{
                    fontSize: '16px',
                    width: '16px',
                    height: '13px',
                    color: theme.colors.blue,
                  }}
                  iconName="fi-rr-users-medical"
                />
                <TextG12 fontWeight="500">Invite Your Neighbors to Meet-Up</TextG12>
              </BlueButton>
              <Modal
                show={showMeetupModal}
                parentCallback={() => setShowMeetupModal(false)}
                modalPadding={0}
                closeButton>
                <MeetupForm place={event.place} event={event} />
              </Modal>
            </Flex>
          )}
        {user?.hasActiveMembership &&
          !(event.id.toString() === musicFestEventId && isWalkaboutEvent) && (
            <>
              <BlueButton
                mt="8px"
                border={`1px solid ${theme.colors.blue}`}
                gap="8px"
                justifyContent="center"
                w="100%"
                textAlign={'center'}
                background={theme.colors.white}
                color={theme.colors.blue}
                onClick={() => {
                  // navigate(`/create-meetup?placeId=${event?.place?.id}&eventId=${event?.id}`);
                  setShowMeetupModal(true);
                }}>
                <Icon
                  style={{
                    fontSize: '16px',
                    width: '16px',
                    height: '13px',
                    color: theme.colors.blue,
                  }}
                  iconName="fi-rr-users-medical"
                />
                <TextG12 fontWeight="500">Invite Your Neighbors to Meet-Up</TextG12>
              </BlueButton>
              <Modal
                show={showMeetupModal}
                parentCallback={() => setShowMeetupModal(false)}
                modalPadding={0}
                closeButton>
                <MeetupForm place={event.place} event={event} />
              </Modal>
            </>
          )}
        {/* "I'm Already Here" option when user appears to be outside check-in distance */}
        {showAlreadyHereOption && (
          <TextG12
            mt={2}
            textDecoration="underline"
            fontWeight="500"
            color={theme.colors.blue}
            cursor="pointer"
            onClick={handleAlreadyHereCheckIn}>
            I'm already here — check me in!
          </TextG12>
        )}
        <IconsHeader event={event} /> <EventCountdown event={event} />
      </Flex>
      {/* <RSVPSelect
        onOptionClick={onClickRSVPModal}
        onModalClose={closeHandler}
        openModal={isOpen}
        event={event}
      /> */}
      <Modal
        isCentered
        show={showInsidersModal}
        parentCallback={() => setShowInsidersModal(false)}
        closeButton>
        <Box p="20px" textAlign="center">
          <H3 mb="15px">Join The Insiders Club</H3>
          <TextG14 mb="20px">
            You need to sign up for the Insiders Club to get access to tickets for this event.
          </TextG14>
          <BlueButton onClick={() => navigate('/memberships')} w="100%" maxW="250px">
            <TextG12 fontWeight="500">View Membership Options</TextG12>
          </BlueButton>
        </Box>
      </Modal>
    </>
  );
};

export default Header;
